import { addDays, addHours, addMinutes, format, startOfHour } from 'date-fns';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { t } from 'i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { Platform, ScrollView, View } from 'react-native';
import Modal from 'react-native-modal';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type { Address } from '@/api/subscriptions/types';
import { DeliveryFeeModal } from '@/components/delivery-fee-modal';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import OrganizationLocationInput from '@/components/organization-location-input';
import { SearchBarButton } from '@/components/search-bar-button';
import mixpanelService from '@/core/clients/mixpanel';
import { useCurrentOrganization } from '@/core/organization';
import useSearchStore from '@/stores/use-search-store';
import { Button, Text } from '@/ui';
import { Calendar } from '@/ui/calendar';
import { CheckItem } from '@/ui/check-item';
import { DummyInput } from '@/ui/dummy-input';
import GoogleAutocomplete from '@/ui/google-autocomplete';
import { Icon } from '@/ui/icon';
import { Building } from '@/ui/icons/building';
import { Calendar as CalendarIcon } from '@/ui/icons/calendar';
import { ListItem } from '@/ui/list-item';

const POPULAR_CITIES = [
  {
    name: 'New York City',
    caption: 'New York, USA',
    icon: <Building />,
    latitude: 40.7128,
    longitude: -74.006,
  },
  {
    name: 'Los Angeles',
    caption: 'California, USA',
    icon: <Building />,
    latitude: 34.0522,
    longitude: -118.2437,
  },
  {
    name: 'Austin',
    caption: 'Texas, USA',
    icon: <Building />,
    latitude: 30.2672,
    longitude: -97.7431,
  },
  {
    name: 'Atlanta',
    caption: 'Georgia, USA',
    icon: <Building />,
    latitude: 33.749,
    longitude: -84.388,
  },
  {
    name: 'San Francisco',
    caption: 'California, USA',
    icon: <Building />,
    latitude: 37.7749,
    longitude: -122.4194,
  },
];

export default function SearchDetails() {
  const { styles, theme } = useStyles(stylesheet);
  const [showCalendar, setShowCalendar] = React.useState(false);
  const deliveryModeEnabled = useSearchStore(
    (state) => state.deliveryModeEnabled
  );
  const setDeliveryModeEnabled = useSearchStore(
    (state) => state.setDeliveryModeEnabled
  );
  const address = useSearchStore((state) => state.address);
  const deliveryDeliverToAddress = useSearchStore(
    (state) => state.deliveryDeliverToAddress
  );
  const setDeliveryDeliverToAddress = useSearchStore(
    (state) => state.setDeliveryDeliverToAddress
  );
  const deliveryReturnAddress = useSearchStore(
    (state) => state.deliveryReturnAddress
  );
  const setDeliveryReturnToAddress = useSearchStore(
    (state) => state.setDeliveryReturnToAddress
  );
  const startDatetime = useSearchStore((state) => state.startDatetime);
  const endDatetime = useSearchStore((state) => state.endDatetime);
  const setAddress = useSearchStore((state) => state.setAddress);
  const setStartDatetime = useSearchStore((state) => state.setStartDatetime);
  const setEndDatetime = useSearchStore((state) => state.setEndDatetime);
  const setCode = useSearchStore((state) => state.setCode);
  const setPromo = useSearchStore((state) => state.setPromo);
  const page = useSearchStore((state) => state.page);
  const [showDeliveryAddressInputModal, setShowDeliveryAddressInputModal] =
    useState(false);
  const [
    showDeliveryReturnAddressInputModal,
    setShowDeliveryReturnAddressInputModal,
  ] = useState(false);
  const router = useRouter();
  const [checkedReturnAddress, setCheckedReturnAddress] = useState(false);
  const [showHowDeliveryFeesModal, setShowHowDeliveryFeesModal] =
    useState(false);
  const [showGoogleAutoComplete, setShowGoogleAutoComplete] = useState(
    !address
  );
  const { organization } = useCurrentOrganization();

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Search Details',
    });
  }, []);

  const local = useLocalSearchParams<{
    start_datetime: string;
    end_datetime: string;
    models?: string;
    code?: string;
    promo?: string;
  }>();

  useEffect(() => {
    if (
      deliveryDeliverToAddress &&
      deliveryReturnAddress &&
      deliveryDeliverToAddress.description === deliveryReturnAddress.description
    ) {
      setCheckedReturnAddress(true);
    }
  }, [deliveryDeliverToAddress, deliveryReturnAddress]);

  const minDate = useMemo(() => {
    return startOfHour(addMinutes(addHours(new Date(), 3), 60));
  }, []);

  const defaultStartDate = useMemo(() => {
    if (startDatetime) {
      return startDatetime;
    }

    return startOfHour(addMinutes(addDays(new Date(), 3), 60));
  }, [startDatetime]);

  const defaultEndDate = useMemo(() => {
    if (endDatetime) {
      return endDatetime;
    }

    return addDays(minDate, 3);
  }, [endDatetime, minDate]);

  React.useEffect(() => {
    if (local.code) {
      setCode(local.code);
    }
  }, [local.code, setCode]);

  React.useEffect(() => {
    if (local.promo) {
      setPromo(local.promo);
    }
  }, [local.promo, setPromo]);

  React.useEffect(() => {
    if (local.start_datetime) {
      setStartDatetime(new Date(local.start_datetime));
    }
  }, [local.start_datetime, setStartDatetime]);

  React.useEffect(() => {
    if (local.end_datetime) {
      setEndDatetime(new Date(local.end_datetime));
    }
  }, [local.end_datetime, setEndDatetime]);

  React.useEffect(() => {
    if (Platform.OS === 'web' && startDatetime) {
      const queryParams = new URLSearchParams(window.location.search);
      queryParams.set(
        'start_datetime',
        format(startDatetime, 'yyyy-MM-dd HH:mm:ss')
      );
      const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
      window.history.replaceState(null, '', newUrl);
    }
  }, [startDatetime]);

  React.useEffect(() => {
    if (Platform.OS === 'web' && endDatetime) {
      const queryParams = new URLSearchParams(window.location.search);
      queryParams.set(
        'end_datetime',
        format(endDatetime, 'yyyy-MM-dd HH:mm:ss')
      );
      const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
      window.history.replaceState(null, '', newUrl);
    }
  }, [endDatetime]);

  const searchDisabled = useMemo(() => {
    if (deliveryModeEnabled) {
      return (
        !startDatetime ||
        !endDatetime ||
        !deliveryDeliverToAddress ||
        !deliveryReturnAddress
      );
    }

    return !startDatetime || !endDatetime || !address;
  }, [
    startDatetime,
    endDatetime,
    address,
    deliveryModeEnabled,
    deliveryDeliverToAddress,
    deliveryReturnAddress,
  ]);

  const searchTimesError = useMemo(() => {
    if (startDatetime === endDatetime) {
      return 'Start and end times cannot be the same.';
    }

    if (endDatetime <= startDatetime) {
      return 'End time must be after the start time.';
    }

    if (startDatetime >= endDatetime) {
      return 'Start time must be before the end time.';
    }

    return '';
  }, [startDatetime, endDatetime]);

  const handleSearch = () => {
    if (deliveryModeEnabled && !deliveryDeliverToAddress) {
      setDeliveryModeEnabled(false);
    }

    const forwardedParams = {
      ...(local.code ? { code: String(local.code) } : {}),
      ...(local.promo ? { promo: String(local.promo) } : {}),
      ...(local.models ? { models: String(local.models) } : {}),
    };

    if (
      deliveryModeEnabled &&
      deliveryDeliverToAddress &&
      deliveryReturnAddress
    ) {
      router.push({
        pathname: '/search',
        params: {
          start_datetime: startDatetime?.toISOString(),
          end_datetime: endDatetime?.toISOString(),
          address: address?.description,
          address_lat: address?.latitude,
          address_lng: address?.longitude,
          delivery_address: deliveryDeliverToAddress.description,
          delivery_address_lat: deliveryDeliverToAddress.latitude,
          delivery_address_lng: deliveryDeliverToAddress.longitude,
          return_address: deliveryReturnAddress.description,
          return_address_lat: deliveryReturnAddress.latitude,
          return_address_lng: deliveryReturnAddress.longitude,
          mode: 'delivery',
          ...forwardedParams,
        },
      });

      return;
    }

    router.push({
      pathname: '/search',
      params: {
        start_datetime: startDatetime?.toISOString(),
        end_datetime: endDatetime?.toISOString(),
        address: address?.description,
        address_lat: address?.latitude,
        address_lng: address?.longitude,
        ...forwardedParams,
      },
    });
  };

  return (
    <View
      style={[
        styles.container,
        { paddingBottom: UnistylesRuntime.insets.bottom },
      ]}
    >
      <View style={{ gap: 24, flex: 1 }}>
        <View style={{ flexDirection: 'row', marginTop: 16, gap: 8 }}>
          <Button
            tx="search.pickup"
            type={deliveryModeEnabled ? 'secondarySmall' : 'primarySmall'}
            onPress={() => {
              setDeliveryModeEnabled(false);
            }}
            style={{ width: '50%' }}
          />

          <Button
            tx="search.delivery"
            type={deliveryModeEnabled ? 'primarySmall' : 'secondarySmall'}
            onPress={() => {
              setDeliveryModeEnabled(true);
            }}
            style={{ width: '50%' }}
          />
        </View>

        {deliveryModeEnabled ? (
          <View style={{ gap: 24 }}>
            <View style={{ gap: 24 }}>
              <View style={{ gap: 16 }}>
                <Text tx="search.when" type="title1" />

                <SearchBarButton
                  onPress={() => setShowCalendar(true)}
                  icon={<CalendarIcon />}
                  title="From"
                  caption={
                    (startDatetime &&
                      endDatetime &&
                      `${format(startDatetime, 'MMM d | h:mm a')} - ${format(
                        endDatetime,
                        'MMM d | h:mm a'
                      )}`) ||
                    undefined
                  }
                />
              </View>

              <View style={{ gap: 16 }}>
                <Text type="title1">Delivery & Return Address</Text>

                <DummyInput
                  onFocus={() => setShowDeliveryAddressInputModal(true)}
                  placeholder={t('search.deliveryAddress')}
                  text={
                    deliveryDeliverToAddress
                      ? deliveryDeliverToAddress.description
                      : null
                  }
                />

                <DummyInput
                  onFocus={() => setShowDeliveryReturnAddressInputModal(true)}
                  placeholder={t('search.returnAddress')}
                  text={
                    deliveryReturnAddress
                      ? deliveryReturnAddress.description
                      : null
                  }
                  disabled={checkedReturnAddress}
                />
              </View>
            </View>

            {deliveryDeliverToAddress ? (
              <View>
                <CheckItem
                  text="Return address is the same address"
                  onPress={() => {
                    if (checkedReturnAddress) {
                      setDeliveryReturnToAddress(null);
                    } else {
                      setDeliveryReturnToAddress(deliveryDeliverToAddress);
                    }

                    setCheckedReturnAddress(!checkedReturnAddress);
                  }}
                  active={checkedReturnAddress}
                  type="checkbox"
                />
              </View>
            ) : null}

            <View>
              <Button
                tx="search.howDeliveryFees"
                type="link"
                onPress={() => {
                  setShowHowDeliveryFeesModal(true);
                }}
                textStyle={{ fontSize: 12 }}
                style={{
                  width: 200,
                  paddingTop: 0,
                  paddingRight: 0,
                  paddingBottom: 0,
                  paddingLeft: 0,
                }}
              />
            </View>
          </View>
        ) : (
          <View style={{ gap: 24, flex: 1 }}>
            <View style={{ gap: 24, zIndex: 1 }}>
              <Text tx="search.whereWhen" type="title1" />

              <View style={{ gap: 16, zIndex: 1 }}>
                <View style={{ flexGrow: 1, zIndex: 1 }}>
                  {organization ? (
                    <OrganizationLocationInput />
                  ) : (
                    <>
                      {!showGoogleAutoComplete ? (
                        <SearchBarButton
                          onPress={() => {
                            setShowGoogleAutoComplete(true);
                          }}
                          title={address ? address.description : 'Where'}
                          caption={address ? '' : 'Address, airport, city...'}
                          icon={<Building />}
                        />
                      ) : null}

                      {showGoogleAutoComplete ? (
                        <GoogleAutocomplete
                          onPress={(data: Address) => {
                            setAddress({
                              description: data.description,
                              placeId: data.placeId,
                              latitude: data.latitude,
                              longitude: data.longitude,
                            });

                            setShowGoogleAutoComplete(false);
                          }}
                          onBlur={() => {
                            // setShowGoogleAutoComplete(false);
                          }}
                          style={{
                            height: 60,
                            // width: '100%',
                            flexGrow: 1,
                          }}
                          autoFocus
                        />
                      ) : null}
                    </>
                  )}
                </View>

                <SearchBarButton
                  onPress={() => setShowCalendar(true)}
                  icon={<CalendarIcon />}
                  title="From"
                  caption={
                    (startDatetime &&
                      endDatetime &&
                      `${format(startDatetime, 'MMM d | h:mm a')} - ${format(
                        endDatetime,
                        'MMM d | h:mm a'
                      )}`) ||
                    undefined
                  }
                />
              </View>
            </View>

            {searchTimesError && (
              <View style={{ alignSelf: 'center' }}>
                <Text
                  style={{
                    color: theme.colors.error,
                    alignSelf: 'flex-start',
                    zIndex: 0,
                  }}
                >
                  {searchTimesError}
                </Text>
              </View>
            )}

            <View
              style={{
                gap: 16,
                flex: 1,
                display: organization ? 'none' : 'flex',
              }}
            >
              <Text tx="search.popularCities" type="title1" />

              <ScrollView
                contentContainerStyle={{
                  gap: 16,
                }}
                style={{ flex: 1 }}
                showsVerticalScrollIndicator={false}
              >
                {POPULAR_CITIES.map((city) => (
                  <ListItem
                    key={city.name}
                    text={city.name}
                    caption={city.caption}
                    icon={
                      <Icon
                        type="dressed"
                        icon={city.icon}
                        fillColor={theme.colors.icon.color}
                        width={25}
                        height={25}
                      />
                    }
                    onPress={() => {
                      setAddress({
                        description: `${city.name}, ${city.caption}`,
                        latitude: city.latitude,
                        longitude: city.longitude,
                      });
                      setShowGoogleAutoComplete(false);
                    }}
                    withBorder={false}
                    style={{
                      borderBottomWidth: 0,
                    }}
                  />
                ))}
              </ScrollView>
            </View>
          </View>
        )}
      </View>

      <View
        style={{
          alignItems: 'center',
          width: '100%',
          height: 60,
        }}
      >
        <Button
          tx="search.search"
          type="primary"
          onPress={handleSearch}
          disabled={searchDisabled || searchTimesError}
        />
      </View>

      <Calendar
        isVisible={showCalendar}
        minDate={minDate}
        defaultStartDate={defaultStartDate}
        defaultEndDate={defaultEndDate}
        onHide={() => {
          setShowCalendar(false);
        }}
        onDatesSelected={(startDt, endDt) => {
          setShowCalendar(false);
          setStartDatetime(startDt);
          setEndDatetime(endDt);
        }}
        onTimeSelected={() => {}}
        range
        id="search-details-calendar"
      />

      <Modal
        animationIn="slideInUp"
        animationInTiming={300}
        animationOut="slideOutDown"
        animationOutTiming={1000}
        isVisible={
          showDeliveryAddressInputModal || showDeliveryReturnAddressInputModal
        }
        hasBackdrop
        style={{
          justifyContent: 'flex-end',
          margin: 0,
        }}
        onBackdropPress={() => {
          if (showDeliveryAddressInputModal) {
            setShowDeliveryAddressInputModal(false);
          }

          if (showDeliveryReturnAddressInputModal) {
            setShowDeliveryReturnAddressInputModal(false);
          }
        }}
      >
        <View
          style={{
            flexGrow: 1,
            maxHeight: '90%',
            backgroundColor: theme.colors.background_2,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            paddingLeft: 16,
            paddingRight: 16,
            gap: 32,
          }}
        >
          <View
            style={{
              alignSelf: 'center',
              marginTop: 16,
              flexDirection: 'row',
              width: '100%',
              justifyContent: 'center',
            }}
          >
            <Text type="title2">
              {showDeliveryAddressInputModal
                ? 'Delivery Address'
                : 'Return Address'}
            </Text>

            <NavigationButton
              type={NavigationButtonTypes.cancel}
              onPress={() => {
                if (showDeliveryAddressInputModal) {
                  setShowDeliveryAddressInputModal(false);
                }

                if (showDeliveryReturnAddressInputModal) {
                  setShowDeliveryReturnAddressInputModal(false);
                }
              }}
              style={{
                position: 'absolute',
                right: 0,
              }}
            />
          </View>

          <View style={{ flexGrow: 1, width: '100%' }}>
            <GoogleAutocomplete
              onPress={(place: Address) => {
                if (showDeliveryAddressInputModal) {
                  setShowDeliveryAddressInputModal(false);
                  setDeliveryDeliverToAddress({
                    ...place,
                  });

                  if (checkedReturnAddress) {
                    setDeliveryReturnToAddress({
                      ...place,
                    });
                  }
                }

                if (showDeliveryReturnAddressInputModal) {
                  setShowDeliveryReturnAddressInputModal(false);

                  setDeliveryReturnToAddress({
                    ...place,
                  });
                }
              }}
              autoFocus
            />
          </View>
        </View>
      </Modal>

      <DeliveryFeeModal
        visible={showHowDeliveryFeesModal}
        onClose={() => {
          setShowHowDeliveryFeesModal(false);
        }}
      />
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background_2,
    gap: 24,
    paddingLeft: 16,
    paddingRight: 16,
    justifyContent: 'space-between',
  },
}));
